import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { useEffect } from 'react';
import { AuthProvider } from './contexts/AuthContext';
import { LanguageProvider } from './contexts/LanguageContext';
import { AnalysisTrackerProvider } from './contexts/AnalysisTrackerContext';
import { Toaster } from 'sonner';
import ProtectedRoute from './components/ProtectedRoute';
import ScrollToTop from './components/ScrollToTop';
import SEOHead from './components/SEOHead';
import LanguageGateway from './components/LanguageGateway';
import GlobalAnalysisProgress from './components/GlobalAnalysisProgress';
import { getHomePath, getInvitePath, getGoogleCompletePath, getLoginPath, getSignupPath, getPricingPath, getTermsPath, getPrivacyPath, getAboutPath } from './utils/localePaths';
import { getStoredLanguage } from './config/languages';

// Pages
import HomePage from './pages/HomePage';
import TopluAnalizPage from './pages/TopluAnalizPage';
import AnalizGecmisiPage from './pages/AnalizGecmisiPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import InvitationAcceptPage from './pages/InvitationAcceptPage';
import GoogleCompleteRegistration from './pages/GoogleCompleteRegistration';
import EmailVerificationPending from './pages/EmailVerificationPending';
import EmailVerificationResult from './pages/EmailVerificationResult';
import ProfilePage from './pages/ProfilePage';
import UpgradePage from './pages/UpgradePage';
import CheckoutPage from './pages/CheckoutPage';
import PaymentMethodPage from './pages/PaymentMethodPage';
import PaymentSuccessPage from './pages/PaymentSuccessPage';
import TermsPage from './pages/TermsPage';
import KVKKPage from './pages/KVKKPage';
import DistanceSalesContractPage from './pages/DistanceSalesContractPage';
import AboutPage from './pages/AboutPage';
import ContactPage from './pages/ContactPage';
import GuvenlikPage from './pages/GuvenlikPage';
import RaporlamaPage from './pages/RaporlamaPage';
import PendingReportsPage from './pages/PendingReportsPage';
import ReportDetailPage from './pages/ReportDetailPage';
import OparaYapsinHavuzPage from './pages/OparaYapsinHavuzPage';
import AdminPage from './pages/AdminPage';
import IntelligenceDashboard from './pages/IntelligenceDashboard';
import './App.css';

/**
 * OPARA v7.1 - Zero-Root Gateway Architecture
 * 
 * Route Structure:
 * - / (root) = ONLY Language Gateway (Splash Screen)
 * - /en/* = English (Global)
 * - /tr/* = Turkish (Production)
 * - /ar/* = Arabic (UAE/Dubai)
 * - /fr/*, /de/*, /es/* = Coming Soon (redirect to gateway)
 * 
 * All content lives under language prefixes. Root is purely a selector.
 */

// ============================================
// ENGLISH ROUTES (/en/*)
// ============================================
const EnglishRoutes = () => (
  <Routes>
    {/* English Home */}
    <Route path="/" element={<HomePage />} />
    
    {/* Auth Routes */}
    <Route path="/login" element={<RegisterPage />} />
    <Route path="/register" element={<RegisterPage />} />
    <Route path="/forgot-password" element={<ForgotPasswordPage />} />
    <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
    <Route path="/verify-email" element={<EmailVerificationResult />} />
    <Route path="/google-complete" element={<GoogleCompleteRegistration />} />
    <Route path="/email-verification" element={<EmailVerificationPending />} />
    <Route path="/invitation" element={<InvitationAcceptPage />} />
    
    {/* Dashboard Routes */}
    <Route path="/bulk-analysis" element={
      <ProtectedRoute>
        <TopluAnalizPage />
      </ProtectedRoute>
    } />
    <Route path="/history" element={<ProtectedRoute><AnalizGecmisiPage /></ProtectedRoute>} />
    <Route path="/settings" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
    <Route path="/pricing" element={<ProtectedRoute><UpgradePage /></ProtectedRoute>} />
    <Route path="/checkout" element={<ProtectedRoute><CheckoutPage /></ProtectedRoute>} />
    <Route path="/payment-method" element={<ProtectedRoute><PaymentMethodPage /></ProtectedRoute>} />
    <Route path="/payment-success" element={<PaymentSuccessPage />} />
    <Route path="/reports" element={<ProtectedRoute><RaporlamaPage /></ProtectedRoute>} />
    <Route path="/pending-reports" element={<ProtectedRoute><PendingReportsPage /></ProtectedRoute>} />
    <Route path="/rescue-pool" element={<ProtectedRoute><OparaYapsinHavuzPage /></ProtectedRoute>} />
    <Route path="/pending-reports/:reportId" element={<ProtectedRoute><ReportDetailPage /></ProtectedRoute>} />
    
    {/* Legal Pages */}
    <Route path="/about" element={<AboutPage />} />
    <Route path="/terms" element={<TermsPage />} />
    <Route path="/privacy" element={<KVKKPage />} />
    <Route path="/contact" element={<ContactPage />} />
    
    {/* Catch all - redirect to English home */}
    <Route path="*" element={<Navigate to="/en/" replace />} />
  </Routes>
);

// ============================================
// TURKISH ROUTES (/tr/*)
// ============================================
const TurkishRoutes = () => (
  <Routes>
    {/* Turkish Home */}
    <Route path="/" element={<HomePage />} />
    <Route path="/opara-yapsin" element={<Navigate to="/tr/toplu-analiz" replace />} />
    
    {/* Toplu Analiz - Protected Route */}
    <Route path="/toplu-analiz" element={
      <ProtectedRoute>
        <TopluAnalizPage />
      </ProtectedRoute>
    } />
    
    {/* Analiz Geçmişi Page */}
    <Route path="/analizgecmisi" element={<ProtectedRoute><AnalizGecmisiPage /></ProtectedRoute>} />
    
    {/* Auth Routes */}
    <Route path="/giris" element={<RegisterPage />} />
    <Route path="/kayit-ol" element={<RegisterPage />} />
    <Route path="/google-kayit-tamamla" element={<GoogleCompleteRegistration />} />
    <Route path="/email-dogrulama" element={<EmailVerificationPending />} />
    <Route path="/verify-email" element={<EmailVerificationResult />} />
    <Route path="/sifremi-unuttum" element={<ForgotPasswordPage />} />
    <Route path="/sifre-sifirla/:token" element={<ResetPasswordPage />} />
    <Route path="/davet-kabul" element={<InvitationAcceptPage />} />
    
    {/* User Profile & Upgrade */}
    <Route path="/hesap-ayarlari" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
    <Route path="/kredi-paketleri" element={<ProtectedRoute><UpgradePage /></ProtectedRoute>} />
    <Route path="/checkout" element={<ProtectedRoute><CheckoutPage /></ProtectedRoute>} />
    <Route path="/odeme" element={<ProtectedRoute><CheckoutPage /></ProtectedRoute>} />
    <Route path="/odeme-yontemi" element={<ProtectedRoute><PaymentMethodPage /></ProtectedRoute>} />
    <Route path="/payment-success" element={<PaymentSuccessPage />} />
    <Route path="/odeme-basarili" element={<PaymentSuccessPage />} />
    <Route path="/rapor" element={<ProtectedRoute><RaporlamaPage /></ProtectedRoute>} />
    <Route path="/bekleyen-raporlar" element={<ProtectedRoute><PendingReportsPage /></ProtectedRoute>} />
    <Route path="/bekleyen-raporlar/:reportId" element={<ProtectedRoute><ReportDetailPage /></ProtectedRoute>} />
    <Route path="/kurtarma-havuzu" element={<ProtectedRoute><OparaYapsinHavuzPage /></ProtectedRoute>} />
    <Route path="/rescue-pool" element={<Navigate to="/tr/kurtarma-havuzu" replace />} />
    
    {/* Turkish Legal Pages */}
    <Route path="/kullanim-kosullari" element={<TermsPage />} />
    <Route path="/kvkk" element={<KVKKPage />} />
    <Route path="/gizlilik" element={<KVKKPage />} />
    <Route path="/mesafeli-satis-sozlesmesi" element={<DistanceSalesContractPage />} />
    <Route path="/hakkimizda" element={<AboutPage />} />
    <Route path="/iletisim" element={<ContactPage />} />
    <Route path="/guvenlik" element={<GuvenlikPage />} />
    
    {/* Catch all - redirect to Turkish home */}
    <Route path="*" element={<Navigate to="/tr/" replace />} />
  </Routes>
);

// ============================================
// ARABIC ROUTES (/ar/*)
// ============================================
const ArabicRoutes = () => (
  <Routes>
    {/* Arabic Home */}
    <Route path="/" element={<HomePage />} />
    
    {/* Bulk Analysis - Protected Route */}
    <Route path="/bulk-analysis" element={
      <ProtectedRoute>
        <TopluAnalizPage />
      </ProtectedRoute>
    } />
    
    {/* Analysis History */}
    <Route path="/history" element={<ProtectedRoute><AnalizGecmisiPage /></ProtectedRoute>} />
    
    {/* Auth Routes */}
    <Route path="/login" element={<RegisterPage />} />
    <Route path="/register" element={<RegisterPage />} />
    <Route path="/google-complete" element={<GoogleCompleteRegistration />} />
    <Route path="/email-verification" element={<EmailVerificationPending />} />
    <Route path="/verify-email" element={<EmailVerificationResult />} />
    <Route path="/forgot-password" element={<ForgotPasswordPage />} />
    <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
    <Route path="/invitation" element={<InvitationAcceptPage />} />
    
    {/* User Profile & Upgrade */}
    <Route path="/settings" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
    <Route path="/pricing" element={<ProtectedRoute><UpgradePage /></ProtectedRoute>} />
    <Route path="/checkout" element={<ProtectedRoute><CheckoutPage /></ProtectedRoute>} />
    <Route path="/payment-method" element={<ProtectedRoute><PaymentMethodPage /></ProtectedRoute>} />
    <Route path="/payment-success" element={<PaymentSuccessPage />} />
    <Route path="/reports" element={<ProtectedRoute><RaporlamaPage /></ProtectedRoute>} />
    <Route path="/pending-reports" element={<ProtectedRoute><PendingReportsPage /></ProtectedRoute>} />
    <Route path="/rescue-pool" element={<ProtectedRoute><OparaYapsinHavuzPage /></ProtectedRoute>} />
    <Route path="/pending-reports/:reportId" element={<ProtectedRoute><ReportDetailPage /></ProtectedRoute>} />
    
    {/* Arabic Legal Pages */}
    <Route path="/terms" element={<TermsPage />} />
    <Route path="/privacy" element={<KVKKPage />} />
    <Route path="/about" element={<AboutPage />} />
    <Route path="/contact" element={<ContactPage />} />
    
    {/* Catch all - redirect to Arabic home */}
    <Route path="*" element={<Navigate to="/ar/" replace />} />
  </Routes>
);

// ============================================
// FRENCH ROUTES (/fr/*)
// ============================================
const FrenchRoutes = () => (
  <Routes>
    <Route path="/" element={<HomePage />} />
    <Route path="/login" element={<RegisterPage />} />
    <Route path="/register" element={<RegisterPage />} />
    <Route path="/forgot-password" element={<ForgotPasswordPage />} />
    <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
    <Route path="/verify-email" element={<EmailVerificationResult />} />
    <Route path="/google-complete" element={<GoogleCompleteRegistration />} />
    <Route path="/email-verification" element={<EmailVerificationPending />} />
    <Route path="/invitation" element={<InvitationAcceptPage />} />
    <Route path="/bulk-analysis" element={<ProtectedRoute><TopluAnalizPage /></ProtectedRoute>} />
    <Route path="/history" element={<ProtectedRoute><AnalizGecmisiPage /></ProtectedRoute>} />
    <Route path="/settings" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
    <Route path="/pricing" element={<ProtectedRoute><UpgradePage /></ProtectedRoute>} />
    <Route path="/checkout" element={<ProtectedRoute><CheckoutPage /></ProtectedRoute>} />
    <Route path="/payment-method" element={<ProtectedRoute><PaymentMethodPage /></ProtectedRoute>} />
    <Route path="/payment-success" element={<PaymentSuccessPage />} />
    <Route path="/reports" element={<ProtectedRoute><RaporlamaPage /></ProtectedRoute>} />
    <Route path="/pending-reports" element={<ProtectedRoute><PendingReportsPage /></ProtectedRoute>} />
    <Route path="/rescue-pool" element={<ProtectedRoute><OparaYapsinHavuzPage /></ProtectedRoute>} />
    <Route path="/pending-reports/:reportId" element={<ProtectedRoute><ReportDetailPage /></ProtectedRoute>} />
    <Route path="/about" element={<AboutPage />} />
    <Route path="/terms" element={<TermsPage />} />
    <Route path="/privacy" element={<KVKKPage />} />
    <Route path="/contact" element={<ContactPage />} />
    <Route path="*" element={<Navigate to="/fr/" replace />} />
  </Routes>
);

// ============================================
// GERMAN ROUTES (/de/*)
// ============================================
const GermanRoutes = () => (
  <Routes>
    <Route path="/" element={<HomePage />} />
    <Route path="/login" element={<RegisterPage />} />
    <Route path="/register" element={<RegisterPage />} />
    <Route path="/forgot-password" element={<ForgotPasswordPage />} />
    <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
    <Route path="/verify-email" element={<EmailVerificationResult />} />
    <Route path="/google-complete" element={<GoogleCompleteRegistration />} />
    <Route path="/email-verification" element={<EmailVerificationPending />} />
    <Route path="/invitation" element={<InvitationAcceptPage />} />
    <Route path="/bulk-analysis" element={<ProtectedRoute><TopluAnalizPage /></ProtectedRoute>} />
    <Route path="/history" element={<ProtectedRoute><AnalizGecmisiPage /></ProtectedRoute>} />
    <Route path="/settings" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
    <Route path="/pricing" element={<ProtectedRoute><UpgradePage /></ProtectedRoute>} />
    <Route path="/checkout" element={<ProtectedRoute><CheckoutPage /></ProtectedRoute>} />
    <Route path="/payment-method" element={<ProtectedRoute><PaymentMethodPage /></ProtectedRoute>} />
    <Route path="/payment-success" element={<PaymentSuccessPage />} />
    <Route path="/reports" element={<ProtectedRoute><RaporlamaPage /></ProtectedRoute>} />
    <Route path="/pending-reports" element={<ProtectedRoute><PendingReportsPage /></ProtectedRoute>} />
    <Route path="/rescue-pool" element={<ProtectedRoute><OparaYapsinHavuzPage /></ProtectedRoute>} />
    <Route path="/pending-reports/:reportId" element={<ProtectedRoute><ReportDetailPage /></ProtectedRoute>} />
    <Route path="/about" element={<AboutPage />} />
    <Route path="/terms" element={<TermsPage />} />
    <Route path="/privacy" element={<KVKKPage />} />
    <Route path="/contact" element={<ContactPage />} />
    <Route path="*" element={<Navigate to="/de/" replace />} />
  </Routes>
);

// ============================================
// SPANISH ROUTES (/es/*)
// ============================================
const SpanishRoutes = () => (
  <Routes>
    <Route path="/" element={<HomePage />} />
    <Route path="/login" element={<RegisterPage />} />
    <Route path="/register" element={<RegisterPage />} />
    <Route path="/forgot-password" element={<ForgotPasswordPage />} />
    <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
    <Route path="/verify-email" element={<EmailVerificationResult />} />
    <Route path="/google-complete" element={<GoogleCompleteRegistration />} />
    <Route path="/email-verification" element={<EmailVerificationPending />} />
    <Route path="/invitation" element={<InvitationAcceptPage />} />
    <Route path="/bulk-analysis" element={<ProtectedRoute><TopluAnalizPage /></ProtectedRoute>} />
    <Route path="/history" element={<ProtectedRoute><AnalizGecmisiPage /></ProtectedRoute>} />
    <Route path="/settings" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
    <Route path="/pricing" element={<ProtectedRoute><UpgradePage /></ProtectedRoute>} />
    <Route path="/checkout" element={<ProtectedRoute><CheckoutPage /></ProtectedRoute>} />
    <Route path="/payment-method" element={<ProtectedRoute><PaymentMethodPage /></ProtectedRoute>} />
    <Route path="/payment-success" element={<PaymentSuccessPage />} />
    <Route path="/reports" element={<ProtectedRoute><RaporlamaPage /></ProtectedRoute>} />
    <Route path="/pending-reports" element={<ProtectedRoute><PendingReportsPage /></ProtectedRoute>} />
    <Route path="/rescue-pool" element={<ProtectedRoute><OparaYapsinHavuzPage /></ProtectedRoute>} />
    <Route path="/pending-reports/:reportId" element={<ProtectedRoute><ReportDetailPage /></ProtectedRoute>} />
    <Route path="/about" element={<AboutPage />} />
    <Route path="/terms" element={<TermsPage />} />
    <Route path="/privacy" element={<KVKKPage />} />
    <Route path="/contact" element={<ContactPage />} />
    <Route path="*" element={<Navigate to="/es/" replace />} />
  </Routes>
);

// ============================================
// ROOT GATEWAY COMPONENT
// Only shows Language Gateway - no other content
// v19.5: Handle OAuth token redirect at root
// ============================================
const RootGateway = () => {
  const navigate = useNavigate();
  
  useEffect(() => {
    // v19.5 FIX: Check for OAuth token at root URL
    const params = new URLSearchParams(window.location.search);
    const tokenFromUrl = params.get('token');

    // Sadece GERCEK OAuth donusu bu dala girsin: backend Google redirect'i
    // (server.py:9958) her zaman provider=google & status=login_success gonderir.
    // Boylece ?token= tasiyan baska linkler (orn. davet) OAuth sanilmaz.
    const isOAuthReturn = params.get('provider') === 'google' || params.get('status') === 'login_success';

    if (tokenFromUrl && isOAuthReturn) {
      // Token found - redirect to the user's own locale home (stored pref survives the
      // OAuth round-trip on the same origin). No stored locale -> getHomePath default.
      const locale = getStoredLanguage();
      const newUrl = `${getHomePath(locale)}?token=${tokenFromUrl}&provider=google&status=login_success`;
      console.log('[RootGateway] OAuth token found, locale:', locale);
      navigate(newUrl, { replace: true });
      return;
    }
  }, [navigate]);
  
  return <LanguageGateway forceShow={true} />;
};

function App() {
  return (
    <Router>
      <ScrollToTop />
      <LanguageProvider>
        <AuthProvider>
          <AnalysisTrackerProvider>
            <SEOHead />
            <Toaster 
              position="top-center" 
              duration={2000} 
              toastOptions={{
                style: { marginTop: '40vh' }
              }} 
              richColors 
              closeButton  /* v30.19: X kapatma butonu eklendi */
            />
            {/* Global Analysis Progress - Shows on ALL pages */}
            <GlobalAnalysisProgress />
            <Routes>
            {/* ============================================ */}
            {/* ROOT - ONLY Language Gateway */}
            {/* ============================================ */}
            <Route path="/" element={<RootGateway />} />
            
            {/* ============================================ */}
            {/* LANGUAGE PREFIXED ROUTES */}
            {/* ============================================ */}
            <Route path="/en/*" element={<EnglishRoutes />} />
            <Route path="/tr/*" element={<TurkishRoutes />} />
            <Route path="/ar/*" element={<ArabicRoutes />} />
            <Route path="/fr/*" element={<FrenchRoutes />} />
            <Route path="/de/*" element={<GermanRoutes />} />
            <Route path="/es/*" element={<SpanishRoutes />} />
            
            {/* ============================================ */}
            {/* LEGACY TURKISH REDIRECTS */}
            {/* Keep for SEO and old bookmarks */}
            {/* ============================================ */}
            {/* Legacy /giris (locale-siz): google-callback hata redirect'lerinin eski hedefi. */}
            {/* Query string'i (?error=...) AYNEN koru; locale'i stored-tercihe gore coz. */}
            <Route path="/giris" element={<Navigate to={`${getLoginPath(getStoredLanguage())}${window.location.search}`} replace />} />
            <Route path="/kayit-ol" element={<Navigate to="/tr/kayit-ol" replace />} />
            <Route path="/toplu-analiz" element={<Navigate to="/tr/toplu-analiz" replace />} />
            <Route path="/analizgecmisi" element={<Navigate to="/tr/analizgecmisi" replace />} />
            <Route path="/hesap-ayarlari" element={<Navigate to="/tr/hesap-ayarlari" replace />} />
            {/* Legacy /kredi-paketleri (locale-siz): stored-tercihe gore coz, query koru (giris deseni). */}
            <Route path="/kredi-paketleri" element={<Navigate to={`${getPricingPath(getStoredLanguage())}${window.location.search}`} replace />} />
            {/* Legacy hukuki (locale-siz): stored-tercihe gore coz, query koru (giris/kredi-paketleri deseni). */}
            <Route path="/kullanim-kosullari" element={<Navigate to={`${getTermsPath(getStoredLanguage())}${window.location.search}`} replace />} />
            <Route path="/kvkk" element={<Navigate to={`${getPrivacyPath(getStoredLanguage())}${window.location.search}`} replace />} />
            <Route path="/hakkimizda" element={<Navigate to={`${getAboutPath(getStoredLanguage())}${window.location.search}`} replace />} />
            <Route path="/iletisim" element={<Navigate to="/tr/iletisim" replace />} />
            <Route path="/sifremi-unuttum" element={<Navigate to="/tr/sifremi-unuttum" replace />} />
            {/* Legacy davet-kabul: eski locale-siz davet mailleri (opara.co/davet-kabul?token=...) */}
            {/* Query string'i (?token=) AYNEN koru; locale'i stored-tercihe gore coz. */}
            <Route path="/davet-kabul" element={<Navigate to={`${getInvitePath(getStoredLanguage())}${window.location.search}`} replace />} />
            {/* Legacy Google kayit-tamamlama: locale-siz backend redirect'i (opara.co/google-kayit-tamamla?session=...) */}
            {/* Query string'i (?session=<uuid>) AYNEN koru; locale'i stored-tercihe gore coz. Catch-all'dan ONCE. */}
            <Route path="/google-kayit-tamamla" element={<Navigate to={`${getGoogleCompletePath(getStoredLanguage())}${window.location.search}`} replace />} />
            <Route path="/google-complete" element={<Navigate to={`${getGoogleCompletePath(getStoredLanguage())}${window.location.search}`} replace />} />
            {/* Legacy /uye-ol (locale-siz): google-callback signup-hata redirect'lerinin ESKI hedefi (route App.js'te YOK'tu -> catch-all). */}
            {/* Query string'i (?error=...) AYNEN koru; locale'i stored-tercihe gore coz -> gercek slug getSignupPath (kayit-ol/register). Catch-all'dan ONCE. */}
            <Route path="/uye-ol" element={<Navigate to={`${getSignupPath(getStoredLanguage())}${window.location.search}`} replace />} />
            <Route path="/opara-yapsin" element={<Navigate to="/tr/toplu-analiz" replace />} />
            <Route path="/rapor" element={<Navigate to="/tr/rapor" replace />} />
            <Route path="/bekleyen-raporlar" element={<Navigate to="/tr/bekleyen-raporlar" replace />} />
            <Route path="/mesafeli-satis-sozlesmesi" element={<Navigate to="/tr/mesafeli-satis-sozlesmesi" replace />} />
            <Route path="/guvenlik" element={<Navigate to="/tr/guvenlik" replace />} />
            
            {/* ============================================ */}
            {/* LEGACY ENGLISH REDIRECTS */}
            {/* Old root-level English URLs redirect to /en/ */}
            {/* ============================================ */}
            <Route path="/login" element={<Navigate to="/en/login" replace />} />
            <Route path="/register" element={<Navigate to="/en/register" replace />} />
            <Route path="/pricing" element={<Navigate to="/en/pricing" replace />} />
            {/* Legacy evrensel-slug hukuki (locale-siz): stored-tercihe gore coz, query koru. */}
            <Route path="/about" element={<Navigate to={`${getAboutPath(getStoredLanguage())}${window.location.search}`} replace />} />
            <Route path="/terms" element={<Navigate to={`${getTermsPath(getStoredLanguage())}${window.location.search}`} replace />} />
            <Route path="/privacy" element={<Navigate to={`${getPrivacyPath(getStoredLanguage())}${window.location.search}`} replace />} />
            <Route path="/contact" element={<Navigate to="/en/contact" replace />} />
            <Route path="/settings" element={<Navigate to="/en/settings" replace />} />
            <Route path="/history" element={<Navigate to="/en/history" replace />} />
            <Route path="/bulk-analysis" element={<Navigate to="/en/bulk-analysis" replace />} />
            <Route path="/forgot-password" element={<Navigate to="/en/forgot-password" replace />} />
            <Route path="/dashboard" element={<Navigate to="/en/" replace />} />
            
            {/* ============================================ */}
            {/* Admin Routes (Language agnostic) */}
            {/* SEC-001: Admin panel URL environment variable'dan okunur */}
            {/* ============================================ */}
            <Route path={`/${process.env.REACT_APP_ADMIN_PATH || 'adminboraorkun'}`} element={<AdminPage />} />
            <Route path="/admin/intelligence" element={<IntelligenceDashboard />} />
            <Route path="/tr/admin/intelligence" element={<IntelligenceDashboard />} />
            <Route path="/en/admin/intelligence" element={<IntelligenceDashboard />} />
            
            {/* ============================================ */}
            {/* Catch all - redirect to gateway */}
            {/* ============================================ */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          </AnalysisTrackerProvider>
        </AuthProvider>
      </LanguageProvider>
    </Router>
  );
}

export default App;
